import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Modal, Nav, Tab, Spinner } from "react-bootstrap";
import { FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";

// Import your forms
import BusinessKyc from "./business_kyc";
import ShareholdingPattern from "./shareholders/shareholding_pattern";
import ShareholdersKyc from "./personal_kyc";

import { useKycData } from "./use_kyc_data";

import "./kyc.css";

const KYC_SUCCESS_STATUSES = ["verified", "completed"];
const KYC_ERROR_STATUSES = ["input_required", "rejected"];

function isKycSuccess(status) {
  return KYC_SUCCESS_STATUSES.includes(status);
}

function isKycError(status) {
  return KYC_ERROR_STATUSES.includes(status);
}

/**
 * Derives sidebar step flags and the default tab for first paint after load.
 */
function computeKycFlowStepState(businessKyc, shareholdersWithKyc, shareholdingCoverage) {
  const merged = shareholdersWithKyc || [];

  const businessKycCompleted =
    !!businessKyc?.uuid && isKycSuccess(businessKyc?.status);

  const shareholdingCompleted =
    (shareholdingCoverage?.coverage_errors || []).length === 0;

  const personalKycCompleted = merged.every((sh) => {
    if (!sh.kyc_required) return true;
    return isKycSuccess(sh.kyc?.status);
  });

  const personalKycError = merged.some((sh) => {
    if (!sh.kyc_required) return false;
    return isKycError(sh.kyc?.status);
  });

  const verifiedStep = [
    {
      value: "business_kyc",
      success: businessKycCompleted,
      error: isKycError(businessKyc?.status),
    },
    {
      value: "shareholding_pattern",
      success: shareholdingCompleted,
      error: false,
    },
    {
      value: "shareholders_kyc",
      success: businessKycCompleted && shareholdingCompleted && personalKycCompleted,
      error: personalKycError,
    },
  ];

  let activeStep = "shareholders_kyc";
  if (!businessKycCompleted) activeStep = "business_kyc";
  else if (!shareholdingCompleted) activeStep = "shareholding_pattern";

  return { verifiedStep, activeStep };
}

function isStepDisabledFromVerified(stepValue, verifiedStep) {
  if (stepValue === "business_kyc") return false;
  if (stepValue === "shareholding_pattern") {
    return !verifiedStep.find((s) => s.value === "business_kyc")?.success;
  }
  if (stepValue === "shareholders_kyc") {
    return !verifiedStep.find((s) => s.value === "shareholding_pattern")?.success;
  }
  return true;
}

const STEP_PREREQUISITE_LABELS = {
  shareholding_pattern: "Complete Business KYC first",
  shareholders_kyc: "Complete Shareholding Pattern first",
};

/** Text shown under a sidebar step so its state isn't conveyed by icon colour alone. */
function getStepCaption(stepValue, stepStatus, disabled) {
  if (disabled) return STEP_PREREQUISITE_LABELS[stepValue] || "Not available yet";
  if (stepStatus?.error) return "Needs your attention";
  if (stepStatus?.success) return "Completed";
  return "";
}

const EMPTY_FLOW_STATE ={ verifiedStep: [], activeStep: "business_kyc" };

export function BusinessKYCModalLayout(props) {
  const { show, setShow, onClose, loanDetails } = props;
  const stepList = [
    { id: 1, value: "business_kyc", label: "Business KYC" },
    { id: 2, value: "shareholding_pattern", label: "Shareholding Pattern" },
    { id: 3, value: "shareholders_kyc", label: "Shareholders KYC" },
  ];
  const closeModal = () => {
    onClose();
    setShow(false);
  }

  const [activeStep, setActiveStep] = useState(stepList[0].value);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const lastStableFlowRef = useRef(null);

  const {
    businessKyc,
    shareholdersWithKyc,
    shareholdingCoverage,
    loading,
    loadError,
    retryLoad,
    businessKycRefreshing,
    shareholdersRefreshing,
    personalKycRefreshing,
    documentsList,
    reloadBusinessKyc,
    reloadShareholders,
    reloadPersonalKyc,
    reloadDocumentsList
  } = useKycData();

  const dataStable =
    !loading &&
    !businessKycRefreshing &&
    !shareholdersRefreshing &&
    !personalKycRefreshing;

  // Derived during render (not in useEffect) so sidebar + goToNextStep see the same snapshot as props.
  // While a reload is in flight, keep showing the last stable snapshot to avoid mixing stale + fresh rows.
  const flowStepState = useMemo(() => {
    if (!dataStable) {
      return lastStableFlowRef.current ?? EMPTY_FLOW_STATE;
    }
    const next = computeKycFlowStepState(
      businessKyc,
      shareholdersWithKyc,
      shareholdingCoverage
    );
    lastStableFlowRef.current = next;
    return next;
  }, [
    dataStable,
    businessKyc,
    shareholdersWithKyc,
    shareholdingCoverage,
  ]);

  const { verifiedStep } = flowStepState;

  useEffect(() => {
    if (!isInitialLoad || !dataStable || loadError) return;
    setActiveStep(flowStepState.activeStep);
    setIsInitialLoad(false);
  }, [isInitialLoad, dataStable, loadError, flowStepState.activeStep]);

  const isStepDisabled = (stepValue) =>
    isStepDisabledFromVerified(stepValue, verifiedStep);

  const goToNextStepRef = useRef(() => {});
  goToNextStepRef.current = () => {
    const { verifiedStep: vs } = flowStepState;
    const currentIndex = stepList.findIndex((step) => step.value === activeStep);
    if (currentIndex < stepList.length - 1) {
      const nextStep = stepList[currentIndex + 1].value;
      if (!isStepDisabledFromVerified(nextStep, vs)) {
        setActiveStep(nextStep);
      }
    } else if (currentIndex === stepList.length - 1) {
      if (
        vs.some(
          (item) =>
            item.value === stepList[currentIndex].value && item.success
        )
      ) {
        closeModal();
      }
    }
  };

  const goToNextStep = useCallback(() => {
    goToNextStepRef.current();
  }, []);

  const stepTabTestIds = {
    business_kyc: "fincobox-kyc-tab-business",
    shareholding_pattern: "fincobox-kyc-tab-shareholding",
    shareholders_kyc: "fincobox-kyc-tab-shareholders",
  };

  return (
    <Modal
      className="anvKyc_tpModal"
      show={show}
      onHide={() => closeModal()}
      backdrop="static"
      keyboard={false}
      size="xl"
      centered
      aria-labelledby="contained-modal-title-vcenter"
      data-testid="fincobox-kyc-modal"
    >
      <Modal.Header>
        <Modal.Title>KYC</Modal.Title>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          data-testid="fincobox-kyc-modal-close"
          onClick={() => closeModal()}
        />
      </Modal.Header>

      <Modal.Body className="kycProc_otrBdy">
        {loading ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center text-center w-100"
            style={{ minHeight: "400px" }}
          >
            <Spinner animation="border" role="status" className="mb-3" />
            <span>Loading KYC Data...</span>
          </div>
        ) : loadError ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center text-center w-100 p-4"
            style={{ minHeight: "400px" }}
            data-testid="fincobox-kyc-load-error"
          >
            <Alert variant="danger" className="mb-3">
              We couldn't load your KYC details. Please check your connection and try again.
            </Alert>
            <Button variant="primary" onClick={retryLoad} data-testid="fincobox-kyc-load-retry">
              Retry
            </Button>
          </div>
        ) : (
          <Tab.Container activeKey={activeStep}>
            <div className="kycProc_sdBar">
              <Nav variant="pills" className="flex-column">
                {stepList.map((step) => {
                  const stepStatusDetails = verifiedStep.find((item) => item.value === step.value);
                  const disabled = isStepDisabled(step.value);
                  const caption = getStepCaption(step.value, stepStatusDetails, disabled);
                  const captionId = `${step.value}_caption`;
                  return (
                    <Nav.Item id={step.value} key={step.value}>
                      <Nav.Link
                        onClick={() => !disabled && setActiveStep(step.value)}
                        className={`d-flex justify-content-between align-items-center ${activeStep === step.value ? "active" : ""
                          } ${disabled ? "disabled" : ""}`}
                        aria-disabled={disabled}
                        aria-describedby={caption ? captionId : undefined}
                        data-testid={stepTabTestIds[step.value]}
                      >
                        <span className="kycStep_labelWrap">
                          <span>{step.label}</span>
                          {caption && (
                            <small id={captionId} className="kycStep_caption">
                              {caption}
                            </small>
                          )}
                        </span>
                        <span aria-hidden="true">
                          {stepStatusDetails?.error && (
                            <FaExclamationTriangle
                              className="warning_icon"
                              color="#FFA500"
                              title={"Rejected"}
                            />
                          )}
                          {stepStatusDetails?.success && (
                            <FaCheckCircle
                              id={`${step.value}_active_check_icon`}
                              className="active_check_icon"
                              color="#21CEB6"
                              data-testid={
                                step.value === "business_kyc"
                                  ? "fincobox-kyc-business-completed"
                                  : undefined
                              }
                            />
                          )}
                        </span>
                      </Nav.Link>
                    </Nav.Item>
                  );
                })}
              </Nav>
            </div>

            <div className="kycProc_sdContent">
              <Tab.Content>
                <Tab.Pane active={activeStep === "business_kyc"}>
                  <BusinessKyc
                    loanDetails={loanDetails}
                    businessKyc={businessKyc}
                    goToNextStep={goToNextStep}
                    reloadBusinessKyc={reloadBusinessKyc}
                    documentsList={documentsList}
                    reloadDocumentsList={reloadDocumentsList}
                  />
                </Tab.Pane>

                <Tab.Pane active={activeStep === "shareholding_pattern"}>
                  <ShareholdingPattern
                    goToNextStep={goToNextStep}
                    shareholders={shareholdersWithKyc}
                    shareholdingCoverage={shareholdingCoverage}
                    reloadShareholders={reloadShareholders}
                  />
                </Tab.Pane>

                <Tab.Pane active={activeStep === "shareholders_kyc"}>
                  <ShareholdersKyc
                    businessKyc={businessKyc}
                    goToNextStep={goToNextStep}
                    reloadPersonalKyc={reloadPersonalKyc}
                    documentsList={documentsList}
                    reloadDocumentsList={reloadDocumentsList}
                    shareholdersWithKyc={shareholdersWithKyc}
                  />
                </Tab.Pane>
              </Tab.Content>
            </div>
          </Tab.Container>
        )}
      </Modal.Body>
    </Modal>
  );
}
